import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, Button, Row, Card, Select, Tooltip, Col, Modal, Pagination, Spin, Table, Space, Image, Tag, Dropdown, message, Input, Switch } from 'antd';
import { DeleteOutlined, FolderAddOutlined, EyeOutlined, DownloadOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { createHash, getImageFallbackUrl } from '../../utils/helpers';
import { useMenuData, useCategories, useSearchProducts } from '../../hooks/useMenuQuery';
import { API } from '../../utils/API';
import { useQueryClient } from '@tanstack/react-query';

const { Option } = Select;

function MenuPaginated({ history }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderComponent, selectedLanguage } = useContext(LayoutContext);

  // State management
  const [selectedItemDelete, setSelectedItemToDelete] = useState();
  const [isExporting, setIsExporting] = useState(false);

  // Category pagination and selection
  const [categoriesCurrentPage, setCategoriesCurrentPage] = useState(1);
  const [categoriesPageSize] = useState(12); // Show 12 categories per page
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [failedCategoryImages, setFailedCategoryImages] = useState(new Set()); // Track failed category images

  // Products pagination for selected category
  const [productsCurrentPage, setProductsCurrentPage] = useState(1);
  const [productsPageSize, setProductsPageSize] = useState(10); // Default 10 per page
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [pendingVisibilityIds, setPendingVisibilityIds] = useState(() => new Set());

  // Fetch hooks
  const { data: menuData, isLoading: isMenuLoading, error: menuError } = useMenuData();
  const menuId = menuData?.id;

  // Fetch paginated categories
  const { data: categoriesData, isLoading: isCategoriesLoading } = useCategories(menuId, categoriesCurrentPage, categoriesPageSize);

  // Create search parameters for products
  const searchParams = useMemo(() => {
    return {
      ...(selectedCategory ? { category_ids: [selectedCategory.id,] } : {}),
      page: productsCurrentPage,
      per_page: productsPageSize,
      ...(debouncedSearchQuery.trim() ? { q: debouncedSearchQuery.trim() } : {}),
    };
  }, [selectedCategory, productsCurrentPage, productsPageSize, debouncedSearchQuery]);

  // Fetch products for selected category using search endpoint (or all products if no category selected)
  const { data: productsData, isLoading: isProductsLoading, isFetching: isFetchingProducts, refetch: refetchProducts } = useSearchProducts(menuId, searchParams);

  const { doFetch: fetchDelete } = useFetch();


  // Don't auto-select categories - let user choose

  // Clear header component
  useEffect(() => {
    setHeaderComponent(null);
    return () => setHeaderComponent(null);
  }, [setHeaderComponent]);

  // Debounce search query to avoid excessive API calls.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Prepare categories for display (without products since they're fetched separately)
  const categoriesForDisplay = useMemo(() => {
    if (!menuData || !categoriesData) return [];

    const lanObj = createHash(menuData.languages, 'id').data;
    // Use selectedLanguage if available, otherwise fallback to first language or English
    const currentLanguage = selectedLanguage || menuData.languages?.[0]?.id;

    return categoriesData.categories?.map(cat => {
      const selectedTranslation = cat.category_translations?.find(
        translation => translation.language_id === currentLanguage
      );

      return {
        id: cat.id,
        image: cat.image_url,
        name: selectedTranslation?.name_text || cat.category_translations?.[0]?.name_text || 'Unnamed',
        nameTranslations: cat.category_translations?.reduce((acc, translation) => {
          acc += `${lanObj[translation.language_id]?.name || 'Unknown'}: ${translation.name_text}<br />`;
          return acc;
        }, '') || '',
      };
    }) || [];
  }, [menuData, categoriesData, selectedLanguage]);

  // Get products for current page and selected category (filtered by backend)
  const currentProducts = productsData?.products || [];

  // Check if selected category is visible on current category page
  const isSelectedCategoryVisible = useMemo(() => {
    if (!selectedCategory || !categoriesData?.categories) return false;
    return categoriesData.categories.some(cat => cat.id === selectedCategory.id);
  }, [selectedCategory, categoriesData]);

  // Helper function to get product name from translations
  const getProductName = (product) => {
    if (!product) return 'Unnamed Product';
    const currentLanguage = selectedLanguage || menuData?.languages?.[0]?.id;
    const translation = product.product_translations?.find(
      t => t.language_id === currentLanguage
    );
    return translation?.name_text || product.product_translations?.[0]?.name_text || 'Unnamed Product';
  };

  // Get selected category name from stored category object
  const selectedCategoryName = useMemo(() => {
    return selectedCategory?.name || null;
  }, [selectedCategory]);


  const handleShowInAppChange = async (record, checked) => {
    setPendingVisibilityIds(prev => new Set(prev).add(record.id));
    queryClient.setQueriesData({ queryKey: ['search-products', menuId] }, (old) => {
      if (!old?.products) return old;
      return {
        ...old,
        products: old.products.map(product => (
          product.id === record.id ? { ...product, show_in_delivery: checked } : product
        )),
      };
    });

    try {
      await API.patch(URLS.updateProduct({ productId: record.id }), {
        show_in_delivery: checked,
      });
      message.success(checked ? 'Product is visible in the app' : 'Product is hidden from the app');
    } catch (error) {
      queryClient.setQueriesData({ queryKey: ['search-products', menuId] }, (old) => {
        if (!old?.products) return old;
        return {
          ...old,
          products: old.products.map(product => (
            product.id === record.id ? { ...product, show_in_delivery: !checked } : product
          )),
        };
      });
      message.error('Could not update app visibility');
    } finally {
      setPendingVisibilityIds(prev => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  };

  const handleItemDelete = () => {
    if (!selectedItemDelete) return;

    fetchDelete({
      url: URLS.deleteProductFetch({
        productId: selectedItemDelete.id,
        categoryId: selectedItemDelete.category_id,
      }),
      method: 'DELETE',
      onSuccess: () => {
        refetchProducts(); // Refresh products after deletion
        setSelectedItemToDelete();
      },
    });
  };


  // Table columns for products
  const productColumns = useMemo(() => {
    // Helper function to get product name from translations
    const getProductName = (product) => {
      if (!product) return 'Unnamed Product';
      const currentLanguage = selectedLanguage || menuData?.languages?.[0]?.id;
      const translation = product.product_translations?.find(
        t => t.language_id === currentLanguage
      );
      return translation?.name_text || product.product_translations?.[0]?.name_text || 'Unnamed Product';
    };

    return [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      responsive: ['sm'], // Hide on mobile
    },
    {
      title: 'Image',
      dataIndex: 'image_url',
      key: 'image',
      width: 80,
      render: (imageUrl, record) => (
        <Link to={`/product/${record.id}`}>
          <Image
            src={imageUrl}
            alt={getProductName(record)}
            width={50}
            height={50}
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback={getImageFallbackUrl(imageUrl)}
            preview={false}
          />
        </Link>
      ),
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Link to={`/product/${record.id}`} style={{ color: '#1890ff', fontWeight: '500' }}>
          {getProductName(record)}
        </Link>
      ),
      ellipsis: true,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price, record) => (
        <span style={{ fontWeight: '500' }}>{price || 0} {record.currency || 'USD'}</span>
      ),
    },
    {
      title: 'In app',
      key: 'show_in_delivery',
      width: 90,
      render: (_, record) => (
        <Tooltip title={record.show_in_delivery === false ? 'Hidden from the delivery app' : 'Shown in the delivery app'}>
          <Switch
            checked={record.show_in_delivery !== false}
            loading={pendingVisibilityIds.has(record.id)}
            onChange={(checked) => handleShowInAppChange(record, checked)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View/Edit">
            <Link to={`/product/${record.id}`}>
              <Button type="primary" icon={<EyeOutlined />} size="small" />
            </Link>
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => setSelectedItemToDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
    ];
  }, [selectedLanguage, menuData, pendingVisibilityIds, menuId]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setProductsCurrentPage(1); // Reset to first page when changing category
  };

  const handleProductsPageChange = (page, size) => {
    setProductsCurrentPage(page);
    if (size !== productsPageSize) {
      setProductsPageSize(size);
    }
  };

  const parseDownloadFilename = (contentDisposition, fallbackFilename) => {
    if (!contentDisposition) return fallbackFilename;
    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return fileNameMatch?.[1] || fallbackFilename;
  };

  const handleExport = async format => {
    if (!menuId || isExporting) return;
    const formatNormalized = (format || 'csv').toLowerCase() === 'tsv' ? 'tsv' : 'csv';
    const loadingMessage = message.loading(`Preparing ${formatNormalized.toUpperCase()} export...`, 0);
    setIsExporting(true);

    try {
      const response = await API.get(URLS.downloadProductsExport({ userId: menuId }), {
        params: { format: formatNormalized },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: response?.headers?.['content-type'] || 'application/octet-stream',
      });
      const fallbackName = `user_${menuId}_products_detailed.${formatNormalized}`;
      const filename = parseDownloadFilename(response?.headers?.['content-disposition'], fallbackName);
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);

      message.success(`Export ready: ${filename}`);
    } catch (error) {
      message.error('Export failed. Please try again.');
    } finally {
      loadingMessage();
      setIsExporting(false);
    }
  };

  const exportMenu = {
    items: [
      {
        key: 'csv',
        label: 'Download CSV',
        onClick: () => handleExport('csv'),
      },
      {
        key: 'tsv',
        label: 'Download TSV',
        onClick: () => handleExport('tsv'),
      },
    ],
  };

  const handleCategoriesPageChange = (page) => {
    setCategoriesCurrentPage(page);
    // Don't reset selected category - user can keep their selection with full object
  };

  // Handle category image error - try fallback URL
  const handleCategoryImageError = (imageUrl) => {
    if (!failedCategoryImages.has(imageUrl)) {
      setFailedCategoryImages(prev => new Set([...prev, imageUrl]));
    }
  };

  const isLoading = isMenuLoading || isCategoriesLoading;

  if (isLoading && !menuData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading menu data...</div>
      </div>
    );
  }

  if (menuError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Error loading menu data: {menuError.message}</div>
      </div>
    );
  }

  return (
    <div className="menu-page-container" style={{ padding: '20px' }}>
      {/* Material Design Linear Progress Bar */}
      {isFetchingProducts && !isProductsLoading && (
        <>
          <style>{`
            @keyframes indeterminate {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(400%);
              }
            }
            @keyframes indeterminate-short {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(300%);
              }
            }
          `}</style>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            zIndex: 9999,
            backgroundColor: '#e0e0e0',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              height: '100%',
              width: '25%',
              backgroundColor: '#1890ff',
              animation: 'indeterminate 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite',
            }} />
            <div style={{
              position: 'absolute',
              height: '100%',
              width: '25%',
              backgroundColor: '#1890ff',
              animation: 'indeterminate-short 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) infinite',
              animationDelay: '1.15s',
            }} />
          </div>
        </>
      )}

      {/* Header Section */}
      {menuData && (
        <div style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 8]} align="middle" justify="space-between">
            <Col xs={24} sm={12}>
              <div>
                <b>Total Categories: {categoriesData?.total || 0}</b>
                {selectedCategory && (
                  <div style={{ marginTop: '4px', fontSize: '14px', color: '#1890ff' }}>
                    Selected: {selectedCategoryName}
                  </div>
                )}
              </div>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
              <Space>
                <Button
                  icon={<FolderAddOutlined />}
                  onClick={() => navigate('/category-create')}
                >
                  Add Category
                </Button>
                <Button
                  type="primary"
                  icon={<FolderAddOutlined />}
                  onClick={() => navigate('/product-create')}
                >
                  Add Product
                </Button>
                <Dropdown menu={exportMenu} trigger={['click']} disabled={!menuId || isExporting}>
                  <Button icon={<DownloadOutlined />} loading={isExporting} disabled={!menuId || isExporting}>
                    {isExporting ? 'Exporting...' : 'Export'} <DownOutlined />
                  </Button>
                </Dropdown>
              </Space>
            </Col>
          </Row>
        </div>
      )}

      {/* Categories Section */}
      <Card title="Categories" style={{ marginBottom: '24px' }}>
        {isCategoriesLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading categories...</div>
          </div>
        ) : (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
              {categoriesForDisplay.map(cat => (
                <Col xs={12} sm={8} md={6} lg={4} xl={3} key={cat.id}>
                  <Card
                    hoverable
                    style={{
                      textAlign: 'center',
                      border: selectedCategory?.id === cat.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      backgroundColor: selectedCategory?.id === cat.id ? '#f6ffed' : '#fff',
                    }}
                    bodyStyle={{
                      padding: '16px 8px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => handleCategoryChange(cat)}
                  >
                    <Avatar
                      size={56}
                      src={failedCategoryImages.has(cat.image) ? getImageFallbackUrl(cat.image) : cat.image}
                      onError={() => handleCategoryImageError(cat.image)}
                      style={{
                        marginBottom: '12px',
                        border: selectedCategory?.id === cat.id ? '2px solid #1890ff' : 'none'
                      }}
                    >
                      {!cat.image && cat.name?.[0]?.toUpperCase()}
                    </Avatar>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: selectedCategory?.id === cat.id ? '600' : '500',
                      color: selectedCategory?.id === cat.id ? '#1890ff' : '#000',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.2',
                      textAlign: 'center'
                    }}>
                      {cat.name}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Categories Pagination */}
            {categoriesData && categoriesData.pages > 1 && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Pagination
                  current={categoriesCurrentPage}
                  total={categoriesData.total || 0}
                  pageSize={categoriesPageSize}
                  onChange={handleCategoriesPageChange}
                  showSizeChanger={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} categories`}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Products Section */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>
              {selectedCategory
                ? `Products in ${selectedCategoryName || 'Selected Category'}`
                : 'All Products'}
            </span>
            {selectedCategory && !isSelectedCategoryVisible && (
              <Tag color="orange">Selected category is not on current page</Tag>
            )}
          </div>
        }
        extra={
          selectedCategory && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setSelectedCategory(null);
                setProductsCurrentPage(1);
              }}
            >
              Clear Filter
            </Button>
          )
        }
      >
        <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={16} md={14} lg={12}>
            <Input
              placeholder="Search products by name (searches in all languages)"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setProductsCurrentPage(1);
              }}
              prefix={<SearchOutlined />}
              allowClear
              onClear={() => {
                setSearchQuery('');
                setProductsCurrentPage(1);
              }}
            />
          </Col>
          {debouncedSearchQuery.trim() && (
            <Col xs={24} sm={8} md={10} lg={12} style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#666' }}>
                Searching for: <strong>{debouncedSearchQuery.trim()}</strong>
              </span>
            </Col>
          )}
        </Row>

        {/* Products Table */}
        <Table
          columns={productColumns}
          dataSource={currentProducts}
          rowKey="id"
          loading={isProductsLoading}
          pagination={false}
          scroll={{ x: 500 }}
          size="small"
          locale={{
            emptyText: isProductsLoading
              ? 'Loading products...'
              : selectedCategory
                ? 'No products found in this category'
                : 'No products found'
          }}
          style={{ marginBottom: '16px' }}
        />

        {/* Products Pagination */}
        {productsData && productsData.pages > 1 && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Pagination
              current={productsCurrentPage}
              total={productsData.total || 0}
              pageSize={productsPageSize}
              onChange={handleProductsPageChange}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={['10', '20', '30', '50', '100']}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} products`}
            />
          </div>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        title={`Delete product: ${getProductName(selectedItemDelete)}`}
        open={!!selectedItemDelete?.id}
        onOk={handleItemDelete}
        onCancel={() => setSelectedItemToDelete()}
        confirmLoading={isProductsLoading}
      >
        <p>Are you sure you want to delete this product? This action cannot be undone.</p>
      </Modal>
    </div>
  );
}

export default MenuPaginated;
